import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { fetchProperties } from 'entities/Property/model/services/fetchProperties/fetchProperties';
import { propertyActions } from 'entities/Property/model/slices/PropertySlice';
import { getRouteForbidden, getRoutePropertiesItem } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { isArray } from 'shared/lib/lodash/lodash';

import { Property } from '../types/PropertySchema';

export const useFetchProperties = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const selectedPropertyId = useSelector(getSelectedPropertyId);

    const initialPropertyIdParam = useRef(null);
    const navigateRef = useRef(null);
    const selectedPropertyIdRef = useRef(selectedPropertyId);
    selectedPropertyIdRef.current = selectedPropertyId;

    useInitialEffect(() => {
        initialPropertyIdParam.current = params.propertyId;
        navigateRef.current = navigate;
    });

    const onFetchProperties = useCallback(
        async (isOrdersPage?: boolean) => {
            const propId = initialPropertyIdParam.current;
            const navigate = navigateRef.current;
            if (propId) {
                dispatch(propertyActions.setPropertyId(propId));
            }

            const response = await dispatch(fetchProperties());

            if (
                propId &&
                isArray(response.payload) &&
                !response.payload.find((prop: Property) => prop.id === propId)
            ) {
                navigate(getRouteForbidden());
                return null;
            }

            if (
                !isOrdersPage &&
                !propId &&
                isArray(response.payload) &&
                response.payload.length > 0
            ) {
                const currentSelectedId = selectedPropertyIdRef.current;
                const isSelectedExists =
                    currentSelectedId &&
                    response.payload.some(
                        (prop: Property) => prop.id === currentSelectedId,
                    );
                const targetId = isSelectedExists
                    ? currentSelectedId
                    : response.payload[0].id;
                navigate(getRoutePropertiesItem(targetId));
            }

            return response;
        },
        [dispatch],
    );

    return onFetchProperties;
};
